import React, { useState, useEffect } from 'react';
import {
  Box,
  CardContent,
  Button,
  Typography,
  Alert,
  InputAdornment,
  Divider,
  Snackbar,
  styled,
  alpha,
} from '@mui/material';
import {
  Email,
  Lock,
  ArrowForward,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import Logo from '../common/Logo';
import { useAuthStore } from '../../store/authStore';
import { LoginCredentials } from '../../types/auth';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import { TwoFactorVerifyDialog } from './TwoFactorVerifyDialog';
import ccLogo from '../../assets/images/cclogo.svg';
import {
  StyledCard,
  StyledTextField,
  PasswordField,
  AuthErrorAlert,
  makeInputChangeHandler,
} from './_shared';

const SSO_ERROR_MESSAGES: Record<string, string> = {
  user_not_found: 'No account found for your Microsoft email. Please contact your administrator to get access.',
  microsoft_auth_failed: 'Microsoft authentication failed. Please try again.',
  microsoft_callback_failed: 'Something went wrong during Microsoft sign-in. Please try again.',
  account_deactivated: 'Your account has been deactivated. Please contact your administrator.',
  no_code_provided: 'Microsoft sign-in was incomplete. Please try again.',
};

const LoginButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    background: theme.palette.grey[300],
  },
}));

const ForgotPasswordButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  outline: 'inherit',
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

interface LoginFormProps {
  onSwitchToSignup: () => void;
  onSuccess?: (user?: any) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToSignup, onSuccess }) => {
  const { login, isLoading, error, clearError, pending2FAUserId, pending2FAEmail, clear2FAState } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [twoFactorVerifyOpen, setTwoFactorVerifyOpen] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setSsoError(SSO_ERROR_MESSAGES[errorParam] || 'An unexpected error occurred during sign-in. Please try again.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleInputChange = (field: keyof LoginCredentials) =>
    makeInputChangeHandler(field, setFormData, validationErrors, setValidationErrors, error, clearError);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await login(formData);

    // Check if 2FA is required
    if (result === '2fa_required') {
      setTwoFactorVerifyOpen(true);
      return;
    }

    // Normal login success
    if (result === true && onSuccess) {
      // Get user from store to pass to onSuccess for redirect logic
      const { user: loggedInUser } = useAuthStore.getState();
      onSuccess(loggedInUser);
    }
  };

  const handle2FASuccess = () => {
    if (onSuccess) {
      // Get user from store after 2FA success
      const { user: loggedInUser } = useAuthStore.getState();
      onSuccess(loggedInUser);
    }
  };

  const handle2FAClose = () => {
    setTwoFactorVerifyOpen(false);
    clear2FAState();
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <StyledCard>
        <CardContent sx={{ p: 5 }}>
          {/* Logo and Header */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 4,
              }}
            >
              <Logo src={ccLogo} width={280} style={{ marginTop: 15, marginBottom: 15 }} />
            </Box>

            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'grey.800',
                letterSpacing: '-0.01em',
                mb: 1,
              }}
            >
              Welcome Back
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                fontSize: '0.95rem',
              }}
            >
              Sign in to your professional carbon management dashboard
            </Typography>
          </Box>

          {/* Error Alert */}
          <AuthErrorAlert message={error} />

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <StyledTextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              disabled={isLoading}
              margin="normal"
              placeholder="name@company.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'grey.500', fontSize: '1.25rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <PasswordField
              fullWidth
              label="Password"
              value={formData.password}
              onChange={handleInputChange('password')}
              error={!!validationErrors.password}
              helperText={validationErrors.password}
              disabled={isLoading}
              margin="normal"
              placeholder="Enter your password"
              showPassword={showPassword}
              onToggleShow={toggleShowPassword}
              isLoading={isLoading}
              iconOrder="hide-when-shown"
              styledToggle
              startAdornment={<Lock sx={{ color: 'grey.500', fontSize: '1.25rem' }} />}
              sx={{ mb: 2 }}
            />

            {/* Remember Me and Forgot Password Row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mb: 3,
              }}
            >
              {/* <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                    sx={{
                      color: 'grey.500',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'grey.700' }}>
                    Remember me
                  </Typography>
                }
              /> */}

              <ForgotPasswordButton
                type="button"
                onClick={() => setForgotPasswordOpen(true)}
                disabled={isLoading}
              >
                Forgot password?
              </ForgotPasswordButton>
            </Box>

            {/* Sign In Button */}
            <LoginButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              endIcon={!isLoading && <ArrowForward />}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </LoginButton>

            {/* Divider */}
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, fontSize: '0.8rem', fontWeight: 500 }}>
                OR CONTINUE WITH
              </Typography>
            </Divider>

            {/* Microsoft Login Button */}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                globalThis.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/auth/microsoft`;
              }}
              startIcon={
                <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <path d="m0 0h10v10h-10z" fill="#f25022" />
                  <path d="m11 0h10v10h-10z" fill="#7fbb00" />
                  <path d="m0 11h10v10h-10z" fill="#00a1f1" />
                  <path d="m11 11h10v10h-10z" fill="#ffb900" />
                </svg>
              }
              sx={{
                borderRadius: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'grey.300',
                color: 'grey.700',
                fontSize: '0.95rem',
                '&:hover': {
                  borderColor: 'grey.400',
                  backgroundColor: 'grey.50',
                },
              }}
            >
              Sign in with Microsoft
            </Button>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'grey.200',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              Protected by enterprise-grade security
            </Typography>
          </Box>
        </CardContent>
      </StyledCard>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
      />

      {/* Two-Factor Verification Dialog */}
      {pending2FAUserId && pending2FAEmail && (
        <TwoFactorVerifyDialog
          open={twoFactorVerifyOpen}
          userId={pending2FAUserId}
          email={pending2FAEmail}
          onClose={handle2FAClose}
          onSuccess={handle2FASuccess}
        />
      )}

      {/* SSO Error Snackbar */}
      <Snackbar
        open={!!ssoError}
        autoHideDuration={8000}
        onClose={() => setSsoError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSsoError(null)}
          severity="error"
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {ssoError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LoginForm;